import React, { useState, useEffect, useRef } from "react";
import { useChanges, useEditorData } from "./AdminDashboard";
import { Change } from "./useChangesTracker";
import { fetchSection, resolveImageSrc, uploadImage } from "../../services/api";
import { resizeImage } from "../../utils/image";
import { useAuth } from "../../contexts/AuthContext";
import pessoasData from "../../pessoas.json";
import { getFieldChanges } from "./deepCompare";
import "./Admin.css";

interface Person {
  id: number;
  foto: string;
  nome: string;
  instituicao: string;
  email: string;
  lattes_url: string;
  bio: {
    pt: string;
    en: string;
    es: string;
  };
  principais_producoes: {
    pt: string[];
    en: string[];
    es: string[];
  };
}

interface PessoasEditorProps {
  onEditingChange?: (isEditing: boolean) => void;
}

const PessoasEditor: React.FC<PessoasEditorProps> = ({ onEditingChange }) => {
  const { changes: changesList, addChange, clearChange, savedSignal } =
    useChanges();
  const { registerEditor, unregisterEditor } = useEditorData();
  const [people, setPeople] = useState<Person[]>([]);
  const [originalPeople, setOriginalPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [activeLang, setActiveLang] = useState<"pt" | "en" | "es">("pt");

  useEffect(() => {
    // Load the live content from the worker, falling back to the bundled copy.
    let cancelled = false;
    (async () => {
      const data = await fetchSection<Person[]>(
        "pessoas",
        pessoasData as Person[]
      );
      if (cancelled) return;
      setPeople(data);
      setOriginalPeople(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // After a successful save, rebaseline to the current data (no page reload,
  // so we never read back not-yet-propagated content from KV).
  const peopleRef = useRef(people);
  peopleRef.current = people;
  useEffect(() => {
    if (savedSignal > 0) setOriginalPeople(peopleRef.current);
  }, [savedSignal]);

  useEffect(() => {
    // Track changes by comparing current state with original
    if (loading) return;

    // Clear all old changes for this section first
    people.forEach((p) => {
      clearChange(`pessoa-${p.id}-add`);
      clearChange(`pessoa-${p.id}-edit`);
      clearChange(`pessoa-${p.id}-delete`);
    });
    originalPeople.forEach((p) => {
      clearChange(`pessoa-${p.id}-add`);
      clearChange(`pessoa-${p.id}-edit`);
      clearChange(`pessoa-${p.id}-delete`);
    });

    // Track new changes
    people.forEach((person) => {
      const original = originalPeople.find((p) => p.id === person.id);
      if (!original) {
        addChange({
          id: `pessoa-${person.id}-add`,
          section: "Pessoas",
          type: "add",
          description: `Adicionar: ${person.nome || "Nova pessoa"}`,
        });
      } else {
        const fieldChanges = getFieldChanges(original, person);
        if (fieldChanges.length > 0) {
          addChange({
            id: `pessoa-${person.id}-edit`,
            section: "Pessoas",
            type: "edit",
            description: `Editar: ${person.nome || "Pessoa sem nome"}`,
            fieldChanges,
          });
        }
      }
    });

    // Track deletions (only for items that existed in original data)
    originalPeople.forEach((person) => {
      if (!people.find((p) => p.id === person.id)) {
        addChange({
          id: `pessoa-${person.id}-delete`,
          section: "Pessoas",
          type: "delete",
          description: `Excluir: ${person.nome || "Pessoa sem nome"}`,
        });
      }
    });
  }, [people, originalPeople, addChange, clearChange, loading]);

  useEffect(() => {
    onEditingChange?.(editingPerson !== null);
  }, [editingPerson, onEditingChange]);

  // Register data getter for AdminDashboard
  useEffect(() => {
    const getData = () => [{ section: "pessoas", data: people }];
    registerEditor("Pessoas", getData);
    return () => unregisterEditor("Pessoas");
  }, [people, registerEditor, unregisterEditor]);

  const addPerson = () => {
    const newId =
      people.length > 0 ? Math.max(...people.map((p) => p.id), 0) + 1 : 1;
    const newPerson: Person = {
      id: newId,
      foto: "",
      nome: "",
      instituicao: "",
      email: "",
      lattes_url: "",
      bio: { pt: "", en: "", es: "" },
      principais_producoes: { pt: [], en: [], es: [] },
    };
    setPeople([...people, newPerson]);
    setEditingPerson(newPerson);
  };

  const updatePerson = (updatedPerson: Person) => {
    setPeople(
      people.map((p) => (p.id === updatedPerson.id ? updatedPerson : p))
    );
    setEditingPerson(null);
  };

  const deletePerson = (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir esta pessoa?")) {
      // Check if this person existed in original data
      const wasOriginal = originalPeople.find((p) => p.id === id);

      setPeople(people.filter((p) => p.id !== id));

      // If it was a newly added person (not in original), clear the add change
      if (!wasOriginal) {
        clearChange(`pessoa-${id}-add`);
        clearChange(`pessoa-${id}-edit`);
      }
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>Gerenciar Pessoas</h2>
        <div>
          <button onClick={addPerson} className="add-button">
            Adicionar Pessoa
          </button>
        </div>
      </div>

      <div className="item-list">
        {people.map((person) => {
          const hasChanges = changesList.some((c: Change) =>
            c.id.includes(`pessoa-${person.id}`)
          );

          return (
            <div
              key={person.id}
              className={`item-card ${hasChanges ? "item-card-changed" : ""}`}
            >
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                {person.foto && (
                  <img
                    src={resolveImageSrc(person.foto)}
                    alt={person.nome}
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #e1e8ed",
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <div>
                  <h3 style={{ margin: "0 0 5px 0" }}>
                    {person.nome || "Sem nome"}
                  </h3>
                  <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
                    {person.email}
                  </p>
                </div>
              </div>
              <div className="item-actions">
                <button
                  onClick={() => setEditingPerson(person)}
                  className="edit-button"
                >
                  Editar
                </button>
                <button
                  onClick={() => deletePerson(person.id)}
                  className="delete-button"
                >
                  Excluir
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editingPerson && (
        <PersonModal
          person={editingPerson}
          onClose={() => setEditingPerson(null)}
          onSave={updatePerson}
          activeLang={activeLang}
          setActiveLang={setActiveLang}
        />
      )}
    </div>
  );
};

export default PessoasEditor;

interface PersonModalProps {
  person: Person;
  onClose: () => void;
  onSave: (person: Person) => void;
  activeLang: "pt" | "en" | "es";
  setActiveLang: (lang: "pt" | "en" | "es") => void;
}

const PersonModal: React.FC<PersonModalProps> = ({
  person,
  onClose,
  onSave,
  activeLang,
  setActiveLang,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Person>(person);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    onSave(formData);
  };

  const handleFile = async (file: File) => {
    if (!user) {
      setUploadError("Sessão expirada. Faça login novamente.");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      // Shrink to a small webp in the browser, then store it in Cloudflare.
      const blob = await resizeImage(file);
      const { url } = await uploadImage(user.token, blob);
      setFormData((fd) => ({ ...fd, foto: url }));
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Falha ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const langs = ["pt", "en", "es"] as const;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Editar Pessoa</h2>

        <div className="form-group">
          <label>Nome</label>
          <input
            type="text"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Foto</label>

          <div
            className={`photo-dropzone${dragging ? " dragging" : ""}${
              uploading ? " busy" : ""
            }`}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
          >
            {formData.foto ? (
              <img
                src={resolveImageSrc(formData.foto)}
                alt="Pré-visualização"
                className="photo-dropzone-preview"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.visibility = "hidden";
                }}
              />
            ) : (
              <div className="photo-dropzone-empty">
                <span className="photo-dropzone-icon">📷</span>
                <span>Arraste uma foto aqui ou clique para escolher</span>
              </div>
            )}
            {uploading && (
              <div className="photo-dropzone-overlay">Enviando…</div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = "";
            }}
          />

          <div className="photo-actions">
            <button
              type="button"
              className="edit-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {formData.foto ? "Trocar foto" : "Escolher foto"}
            </button>
            {formData.foto && (
              <button
                type="button"
                className="delete-button"
                onClick={() => setFormData({ ...formData, foto: "" })}
                disabled={uploading}
              >
                Remover
              </button>
            )}
          </div>

          {uploadError && (
            <div className="error" style={{ marginTop: "8px" }}>
              {uploadError}
            </div>
          )}

          <details className="photo-url-fallback">
            <summary>ou informar uma URL / arquivo existente</summary>
            <input
              type="text"
              value={formData.foto}
              onChange={(e) =>
                setFormData({ ...formData, foto: e.target.value })
              }
              placeholder="https://… ou nome-do-arquivo.jpg"
            />
          </details>
        </div>

        <div className="form-group">
          <label>E-mail</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
        </div>

        <div className="form-group">
          <label>Instituição</label>
          <input
            type="text"
            value={formData.instituicao}
            onChange={(e) =>
              setFormData({ ...formData, instituicao: e.target.value })
            }
          />
        </div>

        <div className="form-group">
          <label>URL Lattes</label>
          <input
            type="url"
            value={formData.lattes_url}
            onChange={(e) =>
              setFormData({ ...formData, lattes_url: e.target.value })
            }
          />
        </div>

        <div className="language-tabs">
          {langs.map((lang) => (
            <button
              key={lang}
              className={`language-tab ${activeLang === lang ? "active" : ""}`}
              onClick={() => setActiveLang(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label>Bio ({activeLang.toUpperCase()})</label>
          <textarea
            value={formData.bio[activeLang]}
            onChange={(e) =>
              setFormData({
                ...formData,
                bio: { ...formData.bio, [activeLang]: e.target.value },
              })
            }
          />
          <small style={{ color: "#6b7785" }}>
            Formatação: **negrito**, *itálico*, [texto](https://link). Linha em
            branco separa parágrafos.
          </small>
        </div>

        <div className="form-group">
          <label>
            Principais Produções ({activeLang.toUpperCase()}) - Uma por linha
          </label>
          <textarea
            value={formData.principais_producoes[activeLang].join("\n")}
            onChange={(e) =>
              setFormData({
                ...formData,
                principais_producoes: {
                  ...formData.principais_producoes,
                  [activeLang]: e.target.value
                    .split("\n")
                    .filter((l) => l.trim()),
                },
              })
            }
          />
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="cancel-button">
            Cancelar
          </button>
          <button onClick={handleSave} className="save-button">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};
